import PortalLayout from '@/components/layout/portal/PortalLayout';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import { Fragment, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/common/Button';
import StatementModal from '@/components/modal/StatementModal';
import PortalBalances from '@/components/portal-modules/PortalBalances';
import PortalBalancesTable, { defaultBalanceSummary } from '@/components/portal-modules/PortalBalancesTable';
import PortalOptionPositionTable from '@/components/portal-modules/PortalOptionPositionTable';
import PortalOverviewControls from '@/components/portal-modules/PortalOverviewControls';
import PortalPortfolio from '@/components/portal-modules/PortalPortfolio';
import PortalPositionTable from '@/components/portal-modules/PortalPositionTable';
import Unauthorized401 from '@/Unauthorized401';
import { useCurrency } from '@/utils/hooks/useCurrency';
import useLanguage from '@/utils/hooks/useLanguage';
import usePortalAuthentication from '@/utils/hooks/usePortalAuthentication';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import { useSso } from '@/utils/providers/SSOProvider';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { useDateFormatter, useNumberFormatter } from 'react-aria';
import { MdOutlineSimCardDownload } from 'react-icons/md';
import Skeleton from 'react-loading-skeleton';

const cardHeight = 122;
const tableHeight = 192;

function XplorPortal() {
    const {
        summaryHistoricDate,
        setSummaryHistoricDate,
        summaryApi,
        marginRequirementsApi,
        lastEODApi,
        foundEntity,
        hasPermission
    } = useSso();

    usePortalAuthentication();

    const lang = useLanguage();
    const screen = useScreenSizes();
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const statementDisclosure = useDisclosure(false);

    const [isHistoric, setIsHistoric] = useState(false);
    const [summaryAge, setSummaryAge] = useState(new Date());

    const { data: summary } = summaryApi;

    const { balances, netEquity, profitLoss, fxOption } = useMemo(() => {
        return {
            balances: summary?.balancesSummary,
            netEquity: summary?.netEquitySummary,
            profitLoss: summary?.profitLossSummary,
            fxOption: summary?.fxOptionSummary
        };
    }, [summary]);

    const unrealizedPnL = useMemo(() => {
        if (profitLoss) {
            return BigNumber(profitLoss.fx.totalInBaseCurrency)
                .plus(profitLoss.cryptoCfd.totalInBaseCurrency)
                .plus(profitLoss.indexCfd.totalInBaseCurrency)
                .plus(profitLoss.futureCfd?.totalInBaseCurrency)
                .plus(profitLoss.commodityCfd?.totalInBaseCurrency);
        }
    }, [profitLoss]);

    const { data: marginRequirements } = marginRequirementsApi;

    const marginValue = useMemo(() => {
        if (marginRequirements) return marginRequirements?.marginRequirements[0]?.valueInBaseCurrency;
    }, [marginRequirements]);

    const availableMargin = useMemo(() => {
        if (netEquity?.netEquity && marginValue) return BigNumber(netEquity.netEquity).minus(marginValue);
    }, [netEquity, marginValue]);

    const marginUsage = useMemo(() => {
        if (netEquity?.netEquity && marginValue)
            return BigNumber(marginValue).dividedBy(netEquity.netEquity).multipliedBy(100);
    }, [netEquity, marginValue]);

    const { baseCurrency, baseCurrencySymbol } = useMemo(() => {
        const baseCurrencyName = marginRequirements?.baseCurrencyName;
        if (baseCurrencyName) {
            return {
                baseCurrency: baseCurrencyName,
                baseCurrencySymbol: baseCurrencyName === 'USD' ? '$' : baseCurrencyName
            };
        } else return { baseCurrency: '', baseCurrencySymbol: '' };
    }, [marginRequirements]);

    const currencyConfig = useCurrency(baseCurrency);
    const maximumFractionDigits = useMemo(() => {
        const value = currencyConfig?.order_decimals - 2;
        return value < 0 ? 0 : value;
    }, [currencyConfig]);

    const formatNumber = useNumberFormatter({ minimumFractionDigits: 0, maximumFractionDigits });
    const formatPercentage = useNumberFormatter({ minimumFractionDigits: 0, maximumFractionDigits: 2 });

    const [startDate, setStartDate] = useState<Date>(new Date());

    const dateFormatter = useDateFormatter();
    const timeFormatter = useDateFormatter({
        timeStyle: 'medium'
    });

    const { data: lastEodData } = lastEODApi;

    const hasOptionPositions = useMemo(() => {
        return fxOption && fxOption.data?.length > 0;
    }, [fxOption]);

    const hasAccountValue = useMemo(() => {
        return (
            !!netEquity?.netEquity &&
            !!marginValue &&
            !!availableMargin &&
            !!balances?.totalInBaseCurrency &&
            !!unrealizedPnL &&
            !!marginUsage
        );
    }, [netEquity?.netEquity, marginValue, availableMargin, balances?.totalInBaseCurrency, unrealizedPnL, marginUsage]);

    const description = useMemo(() => {
        const formattedDate = dateFormatter.format(isHistoric ? startDate : summaryAge);
        const timeString = timeFormatter.format(summaryAge);

        return isHistoric ? `As of ${formattedDate}` : `Last Updated: Today, ${timeString}`;
    }, [isHistoric, startDate, summaryAge, dateFormatter, timeFormatter]);

    useEffect(() => {
        summaryApi.mutate();
        marginRequirementsApi.mutate();
        setSummaryAge(new Date());
    }, [summaryHistoricDate, currentAccount]);

    useEffect(() => {
        if (lastEodData) {
            const lastEodDate = new Date(lastEodData);
            if (startDate > lastEodDate) setStartDate(lastEodDate);
        }
    }, [lastEodData]);

    useEffect(() => {
        setSummaryHistoricDate(undefined);
        lastEODApi.mutate();
    }, []);

    const isAuthorized = hasPermission('ACCOUNT_VIEW');

    const isMargin = useMemo(() => {
        return foundEntity?.attributes?.includes('Margin');
    }, [foundEntity]);

    return credentials ? (
        <PortalLayout isAuthorized={isAuthorized}>
            {isAuthorized && (
                <Fragment>
                    <div className="flex flex-col w-full gap-2 sm:gap-4 p-2 sm:p-6 text-brand-text mt-16 lg:mt-0">
                        <div className="flex flex-wrap md:h-[34px] gap-2 w-full justify-between">
                            <div className="font-bold text-xl">{lang.portal.accountOverview}</div>
                            <Button
                                type="submit"
                                className="h-fit w-fit !gap-2 text-xs"
                                onClick={statementDisclosure[1].open}>
                                <MdOutlineSimCardDownload className="w-4 h-4" />
                                {lang.portal.generateStatement}
                            </Button>
                        </div>
                        <PortalOverviewControls
                            isHistoric={isHistoric}
                            setIsHistoric={setIsHistoric}
                            startDate={startDate}
                            setStartDate={setStartDate}
                            setSummaryAge={setSummaryAge}
                        />
                        {summaryApi.isLoading || summaryApi.isValidating ? (
                            <Fragment>
                                {screen.mobile ? (
                                    <div className="flex flex-col w-full gap-2 sm:gap-4">
                                        <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        <Skeleton containerClassName="flex-1" height={cardHeight} />
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap w-full gap-2 sm:gap-4">
                                        <div className="flex flex-wrap w-full gap-2 sm:gap-4">
                                            <Skeleton containerClassName="flex-1" height={cardHeight} />
                                            <Skeleton containerClassName="flex-1" height={cardHeight} />
                                            <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        </div>
                                        <div className="flex flex-wrap w-full gap-2 sm:gap-4">
                                            <Skeleton containerClassName="flex-1" height={cardHeight} />
                                            <Skeleton containerClassName="flex-1" height={cardHeight} />
                                            <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        </div>
                                        <div className="flex w-full">
                                            <Skeleton containerClassName="flex-1" height={tableHeight} />
                                        </div>
                                        <div className="flex w-full">
                                            <Skeleton containerClassName="flex-1" height={tableHeight} />
                                        </div>
                                    </div>
                                )}
                            </Fragment>
                        ) : (
                            <Fragment>
                                {!isHistoric && isMargin && (
                                    <PortalPortfolio profitLoss={profitLoss} baseCurrency={baseCurrency} />
                                )}
                                {!isMargin && <PortalBalances balances={balances} netEquity={netEquity} />}
                                {hasAccountValue && isMargin && (
                                    <div className="flex flex-wrap w-full gap-2 sm:gap-4">
                                        <BalancesCard
                                            title={lang.accountValue.equity}
                                            description={description}
                                            baseCurrency={baseCurrency}
                                            formatter={formatNumber}>
                                            {netEquity?.netEquity && netEquity.netEquity}
                                        </BalancesCard>
                                        <BalancesCard
                                            title={lang.accountValue.marginUsed}
                                            description={description}
                                            baseCurrency={baseCurrency}
                                            formatter={formatNumber}>
                                            {marginValue && marginValue}
                                        </BalancesCard>
                                        <BalancesCard
                                            title={lang.accountValue.availableMargin}
                                            description={description}
                                            baseCurrency={baseCurrency}
                                            formatter={formatNumber}>
                                            {availableMargin && availableMargin.toNumber()}
                                        </BalancesCard>
                                        <BalancesCard
                                            title={lang.balances.total}
                                            description={description}
                                            baseCurrency={baseCurrency}
                                            formatter={formatNumber}>
                                            {balances?.totalInBaseCurrency && balances.totalInBaseCurrency}
                                        </BalancesCard>
                                        <BalancesCard
                                            title={lang.accountValue.unrealizedPL}
                                            description={description}
                                            baseCurrency={baseCurrency}
                                            formatter={formatNumber}>
                                            {unrealizedPnL && unrealizedPnL.toNumber()}
                                        </BalancesCard>
                                        <BalancesCard
                                            title={lang.accountValue.marginUsage}
                                            description={description}
                                            baseCurrency={baseCurrency}
                                            formatter={formatPercentage}
                                            postfix="%">
                                            {marginUsage && marginUsage.toNumber()}
                                        </BalancesCard>
                                    </div>
                                )}

                                <PortalBalancesTable
                                    balances={balances || defaultBalanceSummary}
                                    baseCurrency={balances?.baseCurrency || ''}
                                    isHistoric={isHistoric}
                                />
                                {isHistoric && isMargin && (
                                    <PortalPortfolio profitLoss={profitLoss} baseCurrency={baseCurrency} />
                                )}
                                {hasAccountValue && isMargin && (
                                    <PortalPositionTable
                                        summary={summary}
                                        baseCurrency={baseCurrency}
                                        isHistoric={isHistoric}
                                    />
                                )}
                                {hasOptionPositions && (
                                    <PortalOptionPositionTable
                                        summary={summary}
                                        baseCurrency={balances?.baseCurrency || ''}
                                        isHistoric={isHistoric}
                                    />
                                )}
                            </Fragment>
                        )}
                    </div>
                    <StatementModal opened={statementDisclosure[0]} handlers={statementDisclosure[1]} />
                </Fragment>
            )}
            {!isAuthorized && <Unauthorized401 />}
        </PortalLayout>
    ) : null;
}

export default XplorPortal;

interface BalancesCardProps {
    children?: number | null;
    className?: string;
    title: string;
    description?: string;
    baseCurrency?: string;
    formatter: Intl.NumberFormat;
    postfix?: string;
}

const BalancesCard = (props: BalancesCardProps) => {
    const isNegative = useMemo(() => {
        if (props.children) {
            const value = props.children;
            return value < 0;
        }
        return false;
    }, [props.children]);

    return (
        <Fragment>
            {!!props.children && (
                <div className="bg-brand-background-dark p-4 sm:p-6 border border-brand-neutral-700 rounded-md w-full sm:flex-[1_0_45%] md:flex-[1_0_30%]">
                    <div className="flex flex-col gap-1 sm:gap-1.5">
                        <div className="text-xs text-brand-neutral-400">{props.title}</div>
                        <div
                            className={cn('sm:text-xl font-semibold', {
                                'text-brand-red': isNegative,
                                'text-brand-green': !isNegative
                            })}>
                            {isNegative && '-'}
                            {(!props.postfix && props.baseCurrency) || ''}{' '}
                            {props.formatter.format(BigNumber(props.children).absoluteValue().toNumber())}
                            {props.postfix || ''}
                        </div>
                        {props.description && <div className="text-xs text-brand-neutral-400">{props.description}</div>}
                    </div>
                </div>
            )}
        </Fragment>
    );
};
